import config from '../config/api';
import logger from '../logging/logger';

export interface WebSocketMessage {
  type: string;
  session_id?: string;
  content?: any;      // string OR object depending on backend
  delta?: string;     // some backends use delta
  data?: any;         // chart payload often lives here
  message?: string;   // error text
  message_id?: string;
  backtest_config_id?: string;
  source_trigger_message_id?: string;
  instrument_type?: 'stock' | 'option';
  symbol?: string;
  side?: 'buy' | 'sell';
  quantity?: number;
  underlying?: string;
  option_legs?: Array<{
    side?: 'buy' | 'sell';
    option_side?: 'call' | 'put';
    expiration?: string;
    strike?: number;
    quantity?: number;
    symbol?: string;
    underlying?: string;
    option_underlying?: string;
  }>;
  order_type?: 'market' | 'limit' | 'best_fill';
  limit_price?: number;
}

export interface MessageHandler {
  onConnectionOpen?: () => void;
  onConnectionClose?: (event: { code: number; reason: string; wasClean: boolean }) => void;
  onConnectionError?: (error: any) => void;
  onMessageStart?: (data: WebSocketMessage) => void;
  onContentDelta?: (data: WebSocketMessage) => void;
  onMessageComplete?: (data: WebSocketMessage) => void;
  onGenerationStopped?: (data: WebSocketMessage) => void;
  onSessionBusy?: (data: WebSocketMessage) => void;
  onSessionReady?: (data: WebSocketMessage) => void;
  onSessionUpdated?: (data: WebSocketMessage) => void;
  onChartData?: (data: WebSocketMessage) => void;
  onBacktestConfig?: (data: WebSocketMessage) => void;
  onManualOrderDraft?: (data: WebSocketMessage) => void;
  onAgentActivity?: (data: WebSocketMessage) => void;
  onError?: (data: WebSocketMessage) => void;
  onRateLimitError?: (data: WebSocketMessage) => void;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandler: MessageHandler | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private sessionId: string | null = null;
  private token: string | null = null;

  connect(sessionId: string, token: string, handler?: MessageHandler) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      logger.info('🌐 WebSocket already connected');
      return;
    }

    this.sessionId = sessionId;
    this.token = token;
    this.messageHandler = handler || null;

    const wsUrl = config.websocketUrl + `?chat_session_id=${sessionId}&token=${token}`;
    logger.info('🔌 Connecting to WebSocket:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      logger.info('🌐 WebSocket connected for session:', sessionId);
      this.reconnectAttempts = 0;
      this.messageHandler?.onConnectionOpen?.();
    };

    this.ws.onclose = (event) => {
      logger.info('🌐 WebSocket disconnected:', event.code, event.reason);
      this.messageHandler?.onConnectionClose?.({
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      if (
        event.code !== 1000 &&
        this.reconnectAttempts < this.maxReconnectAttempts &&
        this.sessionId &&
        this.token
      ) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      logger.error('🌐 WebSocket error:', error);
      this.messageHandler?.onConnectionError?.(error);
    };

    this.ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        this.routeMessage(response);
      } catch (error) {
        logger.error('🌐 Failed to parse WebSocket message:', error, 'Raw data:', event.data);
      }
    };
  }

  private scheduleReconnect() {
    if (!this.sessionId || !this.token) {
      logger.info('🔄 Cannot reconnect: missing session ID or token');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    logger.info(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      if (!this.isConnected() && this.sessionId && this.token) {
        this.connect(this.sessionId, this.token, this.messageHandler || undefined);
      }
    }, delay);
  }

  private routeMessage(response: any) {
    if (!this.messageHandler) return;

    switch (response.type) {
      case 'message_start':
        this.messageHandler.onMessageStart?.(response);
        break;
      case 'content_delta':
        this.messageHandler.onContentDelta?.(response);
        break;
      case 'message_complete':
        this.messageHandler.onMessageComplete?.(response);
        break;
      case 'error':
        this.messageHandler.onError?.(response);
        break;
      case 'generation_stopped':
        this.messageHandler.onGenerationStopped?.(response);
        break;
      case 'session_busy':
        this.messageHandler.onSessionBusy?.(response);
        break;
      case 'session_ready':
        this.messageHandler.onSessionReady?.(response);
        break;
      case 'session_updated':
        this.messageHandler.onSessionUpdated?.(response);
        break;
      case 'chart_data':
        logger.info('📊 [WebSocket] Received chart_data:', response);
        this.messageHandler.onChartData?.(response);
        break;
      case 'backtest_config':
        this.messageHandler.onBacktestConfig?.(response);
        break;
      case 'manual_order_draft':
        this.messageHandler.onManualOrderDraft?.(response);
        break;
      case 'agent_activity':
        this.messageHandler.onAgentActivity?.(response);
        break;
      case 'rate_limit_error':
        this.messageHandler.onRateLimitError?.(response);
        break;
      default:
        logger.info('🌐 Unknown message type:', response.type, response);
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  disconnect() {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.sessionId = null;
    this.token = null;
  }

  sendMessage(message: WebSocketMessage) {
    if (this.isConnected()) {
      logger.info('📤 Sending WebSocket message:', message);
      this.ws?.send(JSON.stringify(message));
    } else {
      logger.error('🌐 Cannot send message: WebSocket not connected');
      throw new Error('WebSocket not connected');
    }
  }
}
